import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectAll", "rowCheckbox", "editButton", "exportButton", "lineItemsOption", "lineItemsOptionDescription", "exportProblemPanel", "exportRequirement", "companyPickerTrigger"]

  connect() {
    this.updateEditButtonState()
    // Bind the method to preserve context and allow proper removal
    this.handleStreamRender = this.handleStreamRender.bind(this)
    // Listen for Turbo Stream updates
    document.addEventListener('turbo:before-stream-render', this.handleStreamRender)
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.handleStreamRender)
  }

  handleStreamRender(event) {
    // Only reset if this is a table body update
    const detail = event.detail
    if (detail?.newStream?.target === 'invoices_table_body') {
      this.resetSelection()
    }
  }

  resetSelection() {
    this.clearExportProblems()

    // Reset select all checkbox
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
    }

    // Reset row checkboxes
    this.rowCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
      checkbox.closest('tr')?.classList.remove('bg-blue-50')
    })

    // Reset edit button state
    this.updateEditButtonState()
  }

  toggleSelectAll(event) {
    // Get all checkboxes except the select all checkbox
    this.rowCheckboxTargets.forEach(checkbox => {
      checkbox.checked = event.target.checked
      const row = checkbox.closest('tr')
      if (row) {
        row.classList.toggle('bg-blue-50', event.target.checked)
      }
    })
    this.updateEditButtonState()
  }

  toggleSelect(event) {
    this.clearExportProblems()

    const checkbox = event.target
    const isChecked = checkbox.checked

    // Sync checkbox state between desktop and mobile views
    // Each invoice has both desktop (table row) and mobile (card) checkboxes.
    // When user clicks one, we sync the other to maintain consistent selection state.

    // Get the invoice ID for the clicked checkbox
    let invoiceId = null
    const row = checkbox.closest('tr')
    const card = checkbox.closest('[data-invoice-id]')

    if (row && row.dataset.invoiceId) {
      invoiceId = row.dataset.invoiceId
    } else if (card && card.dataset.invoiceId) {
      invoiceId = card.dataset.invoiceId
    }

    if (invoiceId) {
      // Find ALL checkboxes for this invoice (both desktop and mobile) and sync their state
      this.rowCheckboxTargets.forEach(targetCheckbox => {
        const targetRow = targetCheckbox.closest('tr')
        const targetCard = targetCheckbox.closest('[data-invoice-id]')

        let targetInvoiceId = null
        if (targetRow && targetRow.dataset.invoiceId) {
          targetInvoiceId = targetRow.dataset.invoiceId
        } else if (targetCard && targetCard.dataset.invoiceId) {
          targetInvoiceId = targetCard.dataset.invoiceId
        }

        // If this checkbox belongs to the same invoice, sync its state
        if (targetInvoiceId === invoiceId) {
          targetCheckbox.checked = isChecked

          // Update visual highlighting for table rows
          if (targetRow) {
            targetRow.classList.toggle('bg-blue-50', isChecked)
          }
        }
      })
    } else {
      // Fallback to original behavior if no invoice ID found
      if (row) {
        row.classList.toggle('bg-blue-50', isChecked)
      }
    }

    // Update select all checkbox and edit button states
    this.updateSelectAllState()
    this.updateEditButtonState()
  }

  updateSelectAllState() {
    // If all checkboxes are checked, check the select all
    // If any checkbox is unchecked, uncheck the select all
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = this.rowCheckboxTargets.every(checkbox => checkbox.checked)
    }
  }

  updateEditButtonState() {
    const selectedInvoices = this.getSelectedInvoices()
    const hasSelection = selectedInvoices.length > 0
    const lineItemsAvailableForSelection = selectedInvoices.every(invoice => invoice.lineItemsAvailable)
    const companyId = new URLSearchParams(window.location.search).get('company_id')

    // Edit only requires selection (modal handles field-level restrictions)
    // Export requires selection AND company context (perspective derived server-side)
    const canEdit = hasSelection
    const canExport = hasSelection && companyId
    const canExportLineItems = canExport
    
    // Update all edit buttons (mobile and desktop)
    this.editButtonTargets.forEach(button => {
      button.disabled = !canEdit
      button.classList.toggle('opacity-50', !canEdit)
      button.classList.toggle('cursor-not-allowed', !canEdit)
      button.classList.toggle('hover:bg-gray-50', canEdit)
      button.classList.toggle('cursor-pointer', canEdit)
    })
    
    // Update all export buttons (mobile and desktop)
    this.exportButtonTargets.forEach(button => {
      button.disabled = !canExport
      button.classList.toggle('opacity-50', !canExport)
      button.classList.toggle('cursor-not-allowed', !canExport)
      button.classList.toggle('hover:bg-gray-50', canExport)
      button.classList.toggle('cursor-pointer', canExport)

      // Update text and icon colors based on state
      const textElement = button.querySelector('span')
      const iconElement = button.querySelector('svg')

      if (textElement) {
        textElement.classList.toggle('text-gray-500', !canExport)
        textElement.classList.toggle('text-gray-700', canExport)
      }

      if (iconElement) {
        iconElement.classList.toggle('text-gray-500', !canExport)
        iconElement.classList.toggle('text-gray-700', canExport)
      }

      if (canExport) {
        button.parentElement.title = ''
      } else if (!hasSelection) {
        button.parentElement.title = 'Pasirinkite sąskaitas'
      } else {
        button.parentElement.title = 'Pasirinkite įmonę eksportui'
      }
    })

    this.exportRequirementTargets.forEach(requirement => {
      requirement.classList.toggle('hidden', canExport || !hasSelection)
      requirement.disabled = !hasSelection
    })

    this.updateLineItemsOptionState({
      hasSelection,
      canExportLineItems,
      lineItemsAvailableForSelection
    })
  }

  openCompanyPicker(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasCompanyPickerTriggerTarget) this.companyPickerTriggerTarget.click()
  }


  // Helper to get selected invoice IDs
  // NOTE: Returns deduplicated list of invoice IDs from all checked checkboxes
  // (handles both desktop table row and mobile card checkboxes)
  getSelectedInvoiceIds() {
    return this.getSelectedInvoices().map(invoice => invoice.id)
  }

  getSelectedInvoices() {
    const selectedInvoices = new Map()

    this.rowCheckboxTargets
      .filter(checkbox => checkbox.checked)
      .forEach(checkbox => {
        const invoiceId = this.getInvoiceIdFromCheckbox(checkbox)
        if (!invoiceId) return

        const lineItemsAvailable = checkbox.dataset.lineItemsAvailable === 'true'
        const previousValue = selectedInvoices.get(invoiceId)

        if (previousValue === undefined) {
          selectedInvoices.set(invoiceId, lineItemsAvailable)
        } else {
          selectedInvoices.set(invoiceId, previousValue && lineItemsAvailable)
        }
      })

    return Array.from(selectedInvoices.entries()).map(([id, lineItemsAvailable]) => ({
      id,
      lineItemsAvailable
    }))
  }

  getInvoiceIdFromCheckbox(checkbox) {
    const row = checkbox.closest('tr')
    if (row && row.dataset.invoiceId) {
      return row.dataset.invoiceId
    }

    const card = checkbox.closest('[data-invoice-id]')
    if (card && card.dataset.invoiceId) {
      return card.dataset.invoiceId
    }

    return null
  }

  updateLineItemsOptionState({ hasSelection, canExportLineItems, lineItemsAvailableForSelection }) {
    if (!this.hasLineItemsOptionTarget) return

    this.lineItemsOptionTargets.forEach(button => {
      button.disabled = !canExportLineItems
      button.classList.toggle('opacity-50', !canExportLineItems)
      button.classList.toggle('cursor-not-allowed', !canExportLineItems)
      button.classList.toggle('hover:bg-gray-50', canExportLineItems)

      if (!hasSelection) {
        button.title = 'Pasirinkite sąskaitas'
      } else if (!lineItemsAvailableForSelection) {
        button.title = 'Eksportas parodys, kurias sąskaitas reikia pataisyti'
      } else {
        button.removeAttribute('title')
      }
    })

    if (this.hasLineItemsOptionDescriptionTarget) {
      this.lineItemsOptionDescriptionTargets.forEach(description => {
        if (!hasSelection) {
          description.textContent = 'Rinkitės tik kai apskaitoje reikia kiekvienos prekės atskirai.'
        } else if (!lineItemsAvailableForSelection) {
          description.textContent = 'Eksportas parodys, kurias sąskaitas reikia pataisyti.'
        } else {
          description.textContent = 'Rinkitės tik kai apskaitoje reikia kiekvienos prekės atskirai.'
        }
      })
    }
  }

  deselectAll() {
    this.clearExportProblems()

    // First uncheck the select all checkbox to ensure clean state
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
    }

    // Then uncheck all row checkboxes
    this.rowCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
      const row = checkbox.closest('tr')
      if (row) {
        row.classList.remove('bg-blue-50')
      }
    })

    this.updateEditButtonState()
  }

  openEditModal(event) {
    event.preventDefault()
    
    // Get selected invoice IDs
    const selectedIds = this.getSelectedInvoiceIds()
    
    // Get custom fields from the data attributes with default empty values
    const customFields = JSON.parse(this.element.dataset.customFields || '[]')
    const vatLineCustomFields = JSON.parse(this.element.dataset.vatLineCustomFields || '[]')
    const existingFieldValues = JSON.parse(this.element.dataset.existingFieldValues || '{}') || {}
    const translations = JSON.parse(this.element.dataset.translations || '{}')
    
    // Company-centric UX: perspective derived from company_id
    const urlParams = new URLSearchParams(window.location.search)
    const hasCompanyId = urlParams.get('company_id')
    const hasPerspectiveContext = !!hasCompanyId

    // Combine all fields and add existing values, with null checks
    const allFields = [...customFields, ...vatLineCustomFields].map(field => ({
      ...field,
      existing_values: (existingFieldValues && existingFieldValues[field.field_name]) || [],
      disabled: field.field_name && !hasPerspectiveContext,
      tooltip: field.field_name && !hasPerspectiveContext ? 'Pasirinkite įmonę filtruose' : null
    }))
    
    // Dispatch custom event to open modal
    const modalEvent = new CustomEvent('modal:open', {
      detail: {
        title: 'Redaguoti sąskaitas',
        fields: allFields,
        translations: translations
      }
    })
    document.querySelector('[data-controller="batch-edit-modal"]').dispatchEvent(modalEvent)
  }

  exportDirectly(event) {
    event.preventDefault()
    this.clearExportProblems()

    // Check if company is selected (perspective derived server-side from company context)
    const companyId = new URLSearchParams(window.location.search).get('company_id')
    if (!companyId) {
      alert('Pasirinkite įmonę filtruose prieš eksportavimą.')
      return
    }

    // Get selected invoice IDs
    const selectedIds = this.getSelectedInvoiceIds()

    if (selectedIds.length === 0) {
      return
    }

    // Show loading state
    this.setExportButtonLoadingState(true)

    // Read export mode from clicked button
    const exportMode = event.currentTarget.dataset.exportMode || 'vat_lines'

    // Submit export directly
    this.submitExport(selectedIds, companyId, exportMode)
  }

  async submitExport(selectedIds, companyId, exportMode = 'vat_lines') {
    // Perspective will be derived server-side from company relationship with invoices

    const formData = new FormData()

    // Add CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      formData.append('authenticity_token', csrfToken)
    }

    // Add invoice IDs
    formData.append('invoice_ids', selectedIds.join(','))

    // Add company_id for server-side perspective derivation
    formData.append('company_id', companyId)

    // Add export mode
    formData.append('export_mode', exportMode)

    // Add current URL parameters, skipping keys already set above
    const reservedParams = new Set(['authenticity_token', 'invoice_ids', 'company_id', 'export_mode'])
    const urlParams = new URLSearchParams(window.location.search)
    for (const [key, value] of urlParams.entries()) {
      if (reservedParams.has(key)) continue
      formData.append(key, value)
    }

    try {
      const response = await fetch('/invoices/export', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        if (Array.isArray(data.problems) && data.problems.length > 0) {
          this.renderExportProblems(data.message, data.problems)
        } else {
          alert(data.message || 'Eksportavimas nepavyko.')
        }
        return
      }

      if (Array.isArray(data.problems) && data.problems.length > 0) {
        this.deselectInvoices(data.exported_invoice_ids || [])
        this.renderExportProblems(data.message, data.problems, data.exported_count || 0)
      } else {
        this.deselectAll()
      }
      window.location.href = data.download_url
    } catch (error) {
      alert('Eksportavimas nepavyko. Prašome pabandyti dar kartą.')
    } finally {
      this.setExportButtonLoadingState(false)
    }
  }

  renderExportProblems(message, problems, exportedCount = 0) {
    if (!this.hasExportProblemPanelTarget) return

    const grouped = this.groupProblemsByInvoice(problems)
    const invoiceCount = grouped.length
    const summary = this.exportProblemSummary(invoiceCount)
    const partialExport = exportedCount > 0
    const title = partialExport ? `Eksportuota: ${exportedCount}` : 'Eksportuoti negalima'
    const blockedStatus = invoiceCount === 1 ? 'neeksportuota' : 'neeksportuotos'
    const description = partialExport
      ? (message || `${summary} neeksportuotos. Peržiūrėkite nurodytas sąskaitas.`)
      : 'Pasirinktos sąskaitos neeksportuotos. Peržiūrėkite nurodytas klaidas ir eksportuokite dar kartą.'

    this.exportProblemPanelTarget.innerHTML = `
      <div class="export-problem-report" role="alert" aria-live="polite">
        <div class="export-problem-report__header">
          <div class="export-problem-report__heading">
            <svg class="export-problem-report__icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4m0 4h.01M10.3 3.9 2.1 18.1A2 2 0 0 0 3.8 21h16.4a2 2 0 0 0 1.7-2.9L13.7 3.9a2 2 0 0 0-3.4 0Z" />
            </svg>
            <div>
              <h2 class="export-problem-report__title">${this.escapeHtml(title)} <span class="export-problem-report__count">${this.escapeHtml(summary)} ${blockedStatus}</span></h2>
              <p class="export-problem-report__summary">${this.escapeHtml(description)}</p>
            </div>
          </div>
          <button type="button"
                  class="export-problem-report__dismiss"
                  aria-label="Slėpti eksporto klaidas"
                  title="Slėpti"
                  data-action="click->invoice-selection#clearExportProblems">
            &times;
          </button>
        </div>
        <ul class="export-problem-report__items">
          ${grouped.map(group => this.problemGroupHtml(group)).join('')}
        </ul>
      </div>
    `
    this.exportProblemPanelTarget.classList.remove('hidden')

    this.highlightProblemRows(grouped.map(group => group.invoiceId))
    this.exportProblemPanelTarget.scrollIntoView({ block: 'nearest', behavior: 'instant' })
  }

  problemGroupHtml(group) {
    const messages = group.problems.map(problem => `
      <span class="export-problem-report__issue">${this.escapeHtml(problem.message)}</span>
    `).join('')
    return `
      <li class="export-problem-report__item">
        <span class="export-problem-report__invoice">${this.escapeHtml(group.invoiceNumber)}</span>
        <span class="export-problem-report__messages">${messages}</span>
        <a class="export-problem-report__fix" href="${this.escapeAttribute(group.editUrl)}" aria-label="Taisyti sąskaitą ${this.escapeAttribute(group.invoiceNumber)}">Taisyti</a>
      </li>
    `
  }

  exportProblemSummary(invoiceCount) {
    const lastDigit = invoiceCount % 10
    const lastTwoDigits = invoiceCount % 100
    let noun = 'sąskaitų'

    if (lastDigit === 1 && lastTwoDigits !== 11) {
      noun = 'sąskaita'
    } else if (lastDigit >= 2 && lastDigit <= 9 && !(lastTwoDigits >= 12 && lastTwoDigits <= 19)) {
      noun = 'sąskaitos'
    }

    return `${invoiceCount} ${noun}`
  }

  groupProblemsByInvoice(problems) {
    const grouped = new Map()
    problems.forEach(problem => {
      const invoiceId = String(problem.invoice_id)
      if (!grouped.has(invoiceId)) {
        grouped.set(invoiceId, {
          invoiceId,
          invoiceNumber: problem.invoice_number || `ID ${invoiceId}`,
          editUrl: problem.edit_url || '#',
          problems: []
        })
      }
      grouped.get(invoiceId).problems.push(problem)
    })
    return Array.from(grouped.values())
  }

  highlightProblemRows(invoiceIds) {
    this.clearProblemRowHighlights()
    invoiceIds.forEach(invoiceId => {
      document.querySelectorAll(`[data-invoice-id="${CSS.escape(String(invoiceId))}"]`).forEach(element => {
        element.classList.add('export-problem-row-highlight')
        element.dataset.exportProblem = 'true'
      })
    })
  }

  clearExportProblems() {
    if (this.hasExportProblemPanelTarget) {
      this.exportProblemPanelTarget.innerHTML = ''
      this.exportProblemPanelTarget.classList.add('hidden')
    }
    this.clearProblemRowHighlights()
  }

  clearProblemRowHighlights() {
    document.querySelectorAll('[data-export-problem="true"]').forEach(element => {
      element.classList.remove('export-problem-row-highlight', 'bg-amber-50', 'ring-1', 'ring-2', 'ring-amber-300', 'ring-inset')
      delete element.dataset.exportProblem
    })
  }

  deselectInvoices(invoiceIds) {
    const exportedIds = new Set(invoiceIds.map(String))

    this.rowCheckboxTargets.forEach(checkbox => {
      if (!exportedIds.has(this.getInvoiceIdFromCheckbox(checkbox))) return

      checkbox.checked = false
      checkbox.closest('tr')?.classList.remove('bg-blue-50')
    })

    this.updateSelectAllState()
    this.updateEditButtonState()
  }

  escapeHtml(value) {
    const div = document.createElement('div')
    div.textContent = value == null ? '' : String(value)
    return div.innerHTML
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;')
  }

  setExportButtonLoadingState(isLoading) {
    this.exportButtonTargets.forEach(button => {
      const textElement = button.querySelector('span')
      const iconElement = button.querySelector('svg')

      if (isLoading) {
        // Disable button and show loading state
        button.disabled = true
        button.classList.add('opacity-50', 'cursor-not-allowed')
        button.classList.remove('hover:bg-gray-50', 'cursor-pointer')

        // Update text
        if (textElement) {
          textElement.textContent = 'Eksportuojama...'
          textElement.classList.add('text-gray-500')
          textElement.classList.remove('text-gray-700')
        }

        // Replace icon with spinner
        if (iconElement) {
          iconElement.innerHTML = `
            <path fill="currentColor" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z" opacity="0.25"/>
            <path fill="currentColor" d="M20 12a8 8 0 0 0-8-8V2a10 10 0 0 1 10 10z">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
            </path>
          `
          iconElement.classList.add('text-gray-500')
          iconElement.classList.remove('text-gray-700')
        }
      } else {
        // Reset button to normal state
        // The updateEditButtonState method will handle proper enabled/disabled state
        this.updateEditButtonState()

        // Reset text
        if (textElement) {
          textElement.textContent = 'Eksportuoti'
        }

        // Reset icon
        if (iconElement) {
          iconElement.innerHTML = `
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
          `
        }
      }
    })
  }
}
